/**
 * Session React Context.
 *
 * The :func:`SessionProvider` populates this from
 * ``GET /api/me/bootstrap``; consumers (chrome, route guards,
 * RBAC-aware components) read it via :func:`useSession`. The
 * preferences slice of the bootstrap response lives in a
 * separate context (``PreferencesContext``) so Stream B can
 * extend it independently.
 *
 * Cross-references:
 *   - REQ-AUTH-022 (BFF-side RBAC; UI mirrors role checks).
 *   - REQ-AUTH-024..027 (role-capability matrix).
 *   - REQ-AUTH-057 (bootstrap response shape).
 */
import { createContext, useContext } from "react";
import type { Role, SessionContextValue } from "@/auth/session-types";

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error(
      "useSession() called outside a <SessionProvider>. Ensure the component tree is wrapped in main.tsx.",
    );
  }
  return ctx;
}

/**
 * Convenience helper for route guards / conditional rendering.
 * Returns false if the user has none of the supplied roles.
 */
export function useHasAnyRole(roles: readonly Role[]): boolean {
  return useSession().hasAnyRole(roles);
}
